// Pointer interaction layer for the v2 planner canvas. Behaviour depends on the
// active tool in the store. Mutations go through the editor (apply for discrete
// edits, commitGesture for drags/transforms) so undo + autosave + dirty-state
// are handled in one place.

import Konva from 'konva';
import { plantDiameter } from '../plants';
import { centerOf, elementInBox } from '../model/bounds';
import { addElements, deleteElements } from '../model/mutators/elements';
import { addSpecies } from '../model/mutators/labels';
import { pasteStyle } from '../model/mutators/props';
import { addVertexAt, removeVertex } from '../model/mutators/paths';
import { insertVertexAt, nearestSegment, pathSegments, newSegmentEntry } from '../model/segments';
import { drawStyleData, isDrawTool } from '../model/draw_style';
import { snapStep } from '../model/grid';
import { coordsToPathData } from './geometry';
import { handleSizes } from './renderer';

const MIN_SIZE = 4;
// Two-finger gestures pan only until the fingers' spread changes by this much
// (so a pan's jitter never zooms); zooming then follows the spread ^ this
// exponent — a touch gentler than 1:1.
const PINCH_ZOOM_DEADZONE = 0.1;
const PINCH_ZOOM_EXP = 0.8;
const PRESS_DRAG_PX = 3; // screen px a redirected press (locked label, covered selection) moves before it drags
// Milliseconds since a performance.now() stamp; never stamped is infinitely
// long ago (performance.now() starts near 0 at page load, so `|| 0` would
// read as "just now" right after load).
const msSince = (t) => (t == null ? Infinity : performance.now() - t);
// How long after a touch mouse events count as its browser-emulated echo (and
// input as "by finger"). The echo normally follows touchend within a few ms
// (no 300ms tap delay with width=device-width); the margin covers a busy main
// thread on a slow phone, kept short so a hybrid device's mouse isn't ignored.
const TOUCH_TRAIL_MS = 500;

export default class Interactions {
  constructor(renderer, store, controller) {
    this.renderer = renderer;
    this.store = store;
    this.controller = controller;
    this.stage = renderer.stage;

    this.draw = null;   // in-progress drag-draw {tool, start}
    this.poly = null;   // in-progress polygon/line {tool, points}
    this.pan = null;    // in-progress pan
    this.lasso = null;  // in-progress lasso
    this._dragPrev = null;
    this._tfPrev = null;
  }

  get tool() { return this.store.state.activeTool; }
  get elements() { return this.store.state.elements; }
  get viewOnly() { return this.controller.viewOnlyValue; }
  get editor() { return this.controller.editor; }

  // Snap to the minor grid intersections (model/grid) — the step follows the zoom.
  snap(v) { const s = snapStep(this.store.state); return s ? Math.round(v / s) * s : v; }
  snapPos(p) { return { x: this.snap(p.x), y: this.snap(p.y) }; }

  bind() {
    const s = this.stage;
    // A tap on empty canvas isn't preventDefault-ed, so the browser follows it
    // with emulated mouse events — Konva would then report every tap twice
    // (tap + click), and two quick taps anywhere as a mouse dblclick. Drop
    // mouse events that trail a touch.
    const touched = () => { this._lastTouchAt = performance.now(); };
    s.container().addEventListener('touchstart', touched, { passive: true, capture: true });
    s.container().addEventListener('touchend', touched, { passive: true, capture: true });
    const real = (fn) => (e) => { if (!this.isEmulatedMouse(e)) fn(e); };
    s.on('mousedown touchstart', real((e) => this.onDown(e)));
    s.on('mousemove touchmove', real((e) => this.onMove(e)));
    s.on('mouseup touchend', real((e) => this.onUp(e)));
    s.on('click tap', real((e) => this.onClick(e)));
    s.on('dblclick dbltap', real((e) => this.onDblClick(e)));
    s.on('wheel', (e) => this.onWheel(e));

    // Touch: two-finger pinch-zoom + long-press context menu.
    s.on('touchstart', (e) => this.onTouchStart(e));
    s.on('touchmove', (e) => this.onTouchMove(e));
    s.on('touchend touchcancel', (e) => this.onTouchEnd(e));

    // Hover highlight (over a shape or its label). Resolved from whatever is
    // under the pointer on every move rather than from mouseover/mouseout
    // pairs: moving from a shape onto its own label (which appears on hover)
    // fires mouseout on the shape, and that used to clear + re-add the hover in
    // a loop — the ring and label flickered. setHover() no-ops when unchanged.
    // No hover while panning or drawing in the editor (nothing under the
    // pointer is the target then), and none for locked (inert) elements. View
    // mode also runs on the navigate tool, but there a click selects and shows
    // Info, so the hover outline stays.
    s.on('mouseover mousemove', real((e) => {
      if (!this.viewOnly && (this.tool === 'navigate' || isDrawTool(this.tool))) { this.renderer.setHover(null); return; }
      const el = this.isElement(e.target) ? this.elementForNode(e.target)
        : this.isLabel(e.target) ? this.elementForLabel(e.target) : null;
      // On the selected polygon/line, hovering an edge shows that segment —
      // a click picks it (v1).
      const seg = this.isElement(e.target) && el ? this.segmentUnderPointer(el) : null;
      if (seg) { this.renderer.setSegmentHover(el, seg.at); return; }
      this.renderer.setHover(el?.data?.locked ? null : el);
    }));
    s.on('mouseleave', () => this.renderer.setHover(null));

    // Right-click: context menu, with a disambiguation chooser when several
    // elements overlap under the pointer (v1 SelectionMenu parity).
    s.on('contextmenu', (e) => {
      e.evt.preventDefault();
      // A touch long-press on a point or box being dragged is not a right-click.
      if (this.gestureActive() || msSince(this._dragEndedAt) < 300) return;
      // On a point of the selected path: its own menu entry ("Remove point").
      const vertex = this.isHandle(e.target) && e.target.getAttr('vkind') === 'anchor'
        ? { id: e.target.getAttr('elId'), index: e.target.getAttr('vidx') } : null;
      if (vertex) {
        const el = this.elements.find((x) => x.id === vertex.id);
        this.controller.openContextMenu(e.evt.clientX, e.evt.clientY, el, { vertex });
        return;
      }
      this.controller.openContextForHits(e.evt.clientX, e.evt.clientY, this.elementsAtPointer(), this.renderer.pointerPlanPos());
    });

    // Node drag (move) — events bubble to the stage.
    // A drag isn't a long-press: Konva swallows the touchmoves that would
    // otherwise cancel the long-press timer.
    // Same for resizing/rotating through the Transformer (not a Konva drag).
    s.on('dragstart', () => this.cancelLongPress());
    s.on('dragend', () => { this._dragEndedAt = performance.now(); });
    this.renderer.transformer.on('transformstart', () => this.cancelLongPress());
    this.renderer.transformer.on('transformend', () => { this._dragEndedAt = performance.now(); });
    s.on('dragstart', (e) => {
      const node = e.target;
      // A second finger makes it a pinch: Konva would otherwise start dragging
      // what the first finger touched, and swallow the touchmoves the pinch
      // needs.
      if (this._pinching) { this.renderer.stopAllDrag(); return; }
      this._pendingDeselect = null; // a drag, not a tap: keep the selection
      // Konva's Transformer auto-drags every attached node when you grab one —
      // ignore those sibling dragstarts (the lead owns the gesture + snapshot).
      if (this._group && this.isElement(node) && node.id() !== this._group.leadId) return;
      if (this.elementForNode(node)?.data?.locked) { node.stopDrag(); return; }
      this._dragPrev = this.editor.takeSnapshot();
      if (this.isHandle(node)) { this.startHandleDrag(node); return; }
      this.startGroupDrag(node);
    });
    s.on('dragmove', (e) => {
      if (this.isHandle(e.target)) { this.renderer.applyHandle(e.target); return; }
      if (this.isElement(e.target)) {
        const n = e.target;
        // In a multi-selection only the element under the pointer (the lead)
        // snaps; the rest move by exactly its delta, so they keep their spacing.
        if (this._group) this.dragGroup();
        else if (snapStep(this.store.state)) n.position({ x: this.snap(n.x()), y: this.snap(n.y()) });
        this.renderer.repositionLabels(); // labels + hover follow the shape
        this.renderer.offsetVertices(n); // poly/line vertex handles follow too
      }
      // dragging a label: Konva moves it freely; don't reposition it back
    });
    s.on('dragend', (e) => this.onDragEnd(e));

    // Transform (resize/rotate) via the Transformer.
    // Vertex handles can't follow a box resize point by point, so they hide for
    // the gesture; the commit's re-render brings them back where they belong.
    this.renderer.transformer.on('transformstart', () => {
      if (this._pinching) { this.renderer.transformer.stopTransform(); return; }
      this._pressTransformed = true; // a resize/rotate, not a click
      this._tfPrev = this.editor.takeSnapshot();
      this.renderer.vertexGroup.visible(false);
    });
    this.renderer.transformer.on('transform', () => this.renderer.repositionLabels());
    this.renderer.transformer.on('transformend', () => this.onTransformEnd());
  }

  elementForNode(node) {
    const id = node?.id?.();
    return id ? this.elements.find((el) => el.id === id) : null;
  }

  // Every element under the pointer, topmost first — real per-shape hit tests
  // (curves included), so buried elements in dense plans stay reachable.
  // Locked nodes don't listen, so they never show up here.
  elementsAtPointer() {
    const pos = this.stage.getPointerPosition();
    if (!pos) return [];
    const seen = new Set(); const els = [];
    for (const node of this.stage.getAllIntersections(pos).reverse()) {
      if (!this.isElement(node)) continue;
      const el = this.elementForNode(node);
      if (el && !seen.has(el.id)) { seen.add(el.id); els.push(el); }
    }
    return els;
  }

  isSoleSelectedPath(el) {
    const sel = this.store.state.selection;
    return !!el && (el.type === 'polygon' || el.type === 'line') && !el.data?.locked && sel.length === 1 && sel[0] === el.id;
  }

  // The segment of the selected path near the pointer (v1's tolerance:
  // 8px, or half the stroke + 4px), as { seg, at } — `at` indexes
  // pathSegments() — or null.
  segmentUnderPointer(el) {
    if (!this.isSoleSelectedPath(el) || this.viewOnly || this.tool !== 'select') return null;
    const pos = this.renderer.pointerPlanPos();
    if (!pos) return null;
    const closed = el.type === 'polygon';
    const hit = nearestSegment(el.data.coordinates || [], closed, pos.x, pos.y);
    if (!hit) return null;
    const scale = this.store.state.view.scale || 1;
    const stroke = Number(el.data?.strokeWidth) > 0 ? el.data.strokeWidth * scale : 1;
    if (hit.distance * scale > Math.max(8, stroke / 2 + 4)) return null;
    const at = pathSegments(el.data.coordinates || [], closed).findIndex((sg) => sg.index === hit.seg.index);
    return { seg: hit.seg, at };
  }

  // A handle drag begins. A midpoint first becomes a real point (split in the
  // middle, curves keeping their shape) and is then dragged as that point.
  // The other handles are rebuilt around the dragged one: a split shifts
  // their indices, and midpoints hide until the drag ends.
  startHandleDrag(node) {
    const el = this.elements.find((x) => x.id === node.getAttr('elId'));
    if (!el || el.data?.locked) { node.stopDrag(); return; }
    if (node.getAttr('vkind') === 'mid') {
      const split = insertVertexAt(el.data.coordinates, el.type === 'polygon', node.getAttr('vidx'));
      if (!split) { node.stopDrag(); return; }
      el.data.coordinates = split.coordinates;
      node.setAttrs({ vkind: 'anchor', vidx: split.vertex, radius: handleSizes().dot / (this.store.state.view.scale || 1), fill: '#ffffff', opacity: 1 });
      this.store.state.selectedSegment = null;
    }
    this.renderer.renderVertices(this.store.state, { keep: node });
    this.renderer.applyHandle(node);
  }

  isElement(node) { return node && node.name && node.name() === 'element'; }
  isLabel(node) { return node && node.name && node.name() === 'label'; }
  isHandle(node) { return node && node.name && node.name() === 'vhandle'; }
  // The resize box: its anchors, side bands and rotater (Konva drives those).
  isTransformerPart(node) { return !!node && node.getParent?.() === this.renderer.transformer; }
  elementForLabel(node) { const id = node?.getAttr?.('elId'); return id ? this.elements.find((el) => el.id === id) : null; }

  // --- pointer down ---------------------------------------------------------
  onDown(e) {
    if (e.evt.touches && e.evt.touches.length > 1) return; // multi-touch -> pinch
    // What was selected before this finger landed (before the press-to-select
    // below): if a second finger turns it into a pinch, that's undone.
    if (e.evt.touches) this._touchSelection = this.store.state.selection.slice();
    // Remember the tool the gesture started with: finishing a drawing switches
    // to select before the browser's click arrives (see onClick).
    this.pointerDownTool = this.tool;
    if (this.viewOnly) { this.startPan(); return; } // drag to pan; no editing
    const node = e.target;
    const onElement = this.isElement(node);
    const pos = this.renderer.pointerPlanPos();
    this._pressOnTransformer = this.isTransformerPart(node);
    this._pressTransformed = false;

    switch (this.tool) {
      case 'rectangle':
      case 'ellipse':
        this.startDraw(this.tool, pos); // anywhere, on top of other shapes too
        break;
      case 'navigate':
        this.startPan();
        break;
      case 'select': {
        // Select an element on press (not click) so an immediate drag already has
        // the transformer + group. Labels are skipped here — re-selecting rebuilds
        // the label node and would kill its drag; they select on click instead.
        if (onElement) {
          const el = this.elementForNode(node);
          // Picking a segment takes a click on a path that was already
          // selected — the click that selects it doesn't also pick one.
          this._pressOnSelectedPath = this.isSoleSelectedPath(el);
          if (el && !el.data?.locked) {
            const additive = this.isAdditive(e.evt);
            const covered = !additive && !this.store.state.selection.includes(el.id) && this.selectedUnderPointer();
            this._pendingDeselect = null;
            if (covered) this.pressCovered(node, covered);
            // Additive press on an already-selected element: it might be the
            // start of dragging the whole selection, so it stays selected —
            // only a tap (click, no drag) takes it out (see onClick).
            else if (additive && this.store.state.selection.includes(el.id)) this._pendingDeselect = el.id;
            else if (additive) this.select(el.id, true);
            else if (!this.store.state.selection.includes(el.id)) this.select(el.id, false);
            // else already selected — keep the (possibly multi-) selection for dragging
          }
        } else if (this.isLabel(node) && this.store.state.settings?.lockLabels === true) {
          // A locked label can't move, so pressing on it drags its shape
          // instead — once the pointer actually moves (see onMove), so a plain
          // click still just selects.
          const el = this.elementForLabel(node);
          if (el && !el.data?.locked) this._labelPress = { el, additive: this.isAdditive(e.evt), start: this.stage.getPointerPosition() };
        } else if (!this.isLabel(node) && !this.isHandle(node) && !this._pressOnTransformer && !this.poly) {
          this.startLasso(pos, e.evt);
        }
        break;
      }
      default:
        break;
    }
  }

  // Shift/meta/ctrl click — or the mobile multi-select toggle — adds to the selection.
  isAdditive(evt) {
    return evt?.shiftKey || evt?.metaKey || evt?.ctrlKey || this.store.state.touchMultiSelect;
  }

  onMove(e) {
    if (this._pinching) return; // a pinch owns the gesture
    if (this._labelPress) { this.dragFromLabel(); return; }
    if (this._coveredPress) { this.dragCovered(); return; }
    const pos = this.renderer.pointerPlanPos();
    if (this.draw) this.updateDraw(pos, e?.evt);
    else if (this.poly) this.updatePoly(pos);
    else if (this.pan) this.updatePan();
    else if (this.lasso) this.updateLasso(pos);
    else if (this.tool === 'placement') this.updatePlacementGhost(pos);
    else if (this.tool === 'calibrate' && this._calib?.length === 1) this.updateCalibLine(pos);
  }

  updateCalibLine(cursor) {
    const a = this._calib[0];
    this.renderer.setPreview(new Konva.Line({
      points: [a.x, a.y, cursor.x, cursor.y],
      stroke: '#087332', strokeWidth: 2, strokeScaleEnabled: false, dash: [6, 4],
    }));
  }

  // A selected element under the pointer, below the (unselected) one on top.
  selectedUnderPointer() {
    const sel = new Set(this.store.state.selection);
    if (!sel.size) return null;
    return this.elementsAtPointer().find((x) => sel.has(x.id) && !x.data?.locked) || null;
  }

  // Pressing on an element that covers a selected one: a drag moves the
  // selection (not the element on top), a plain click still selects the top
  // one (onClick). Konva has already readied the top node's drag — turning
  // its dragging off drops that; it's back on when the press ends.
  pressCovered(topNode, selected) {
    topNode.draggable(false);
    this._coveredPress = { topNode, selected, start: this.stage.getPointerPosition() };
  }

  dragCovered() {
    const { selected, start } = this._coveredPress;
    const p = this.stage.getPointerPosition();
    if (!p || Math.hypot(p.x - start.x, p.y - start.y) < PRESS_DRAG_PX) return;
    this.endCoveredPress();
    this.renderer.nodeMap.get(selected.id)?.startDrag();
  }

  endCoveredPress() {
    if (!this._coveredPress) return;
    const { topNode } = this._coveredPress;
    this._coveredPress = null;
    const el = this.elementForNode(topNode);
    if (el && !el.data?.locked) topNode.draggable(this.renderer.draggable);
  }

  // Past a small threshold, hand a locked-label press over to a regular drag
  // of the label's shape (selected first, so a multi-selection moves together).
  dragFromLabel() {
    const { el, additive, start } = this._labelPress;
    const p = this.stage.getPointerPosition();
    if (!p || Math.hypot(p.x - start.x, p.y - start.y) < PRESS_DRAG_PX) return;
    this._labelPress = null;
    if (!this.store.state.selection.includes(el.id)) this.select(el.id, additive);
    this.renderer.nodeMap.get(el.id)?.startDrag();
  }

  onUp() {
    this._labelPress = null;
    this.endCoveredPress();
    if (this.draw) this.finishDraw();
    else if (this.pan) this.pan = null;
    else if (this.lasso) this.finishLasso();
  }

  // --- click (selection / erase / poly vertices) ----------------------------
  onClick(e) {
    if (this._lpFired) { this._lpFired = false; return; } // long-press already handled
    // The tap Konva reports as a pinch's fingers lift isn't a click.
    if (this.pinchJustEnded()) return;
    // A marquee ends with mouseup, and the browser then fires a click on the
    // stage — without this guard that click's deselect branch would wipe the
    // selection the marquee just made.
    if (this._suppressClick) { this._suppressClick = false; return; }
    if (this.viewOnly) { this.onViewClick(e); return; }
    // The click that ends a drag-drawn shape lands after finishDraw switched to
    // select — it must not deselect the shape that was just created.
    const pointerDownTool = this.pointerDownTool;
    this.pointerDownTool = null;
    if (this.tool === 'select' && pointerDownTool && pointerDownTool !== 'select') return;
    const node = e.target;
    if (this.isHandle(node)) {
      // A click on a midpoint adds the point without dragging it; other
      // handles never deselect.
      if (node.getAttr('vkind') === 'mid') {
        this.editor.apply(addVertexAt, { id: node.getAttr('elId'), index: node.getAttr('vidx') });
      }
      return;
    }
    const onElement = this.isElement(node);

    if (this.tool === 'placement') {
      const pos = this.snapPos(this.renderer.pointerPlanPos());
      this.controller.placeSpecies(pos.x, pos.y, e.evt.shiftKey); // Shift = keep placing
      return;
    }

    if (this.tool === 'calibrate') {
      const pos = this.renderer.pointerPlanPos();
      this._calib = this._calib || [];
      this._calib.push({ x: pos.x, y: pos.y });
      if (this._calib.length >= 2) {
        const [a, b] = this._calib; this._calib = null; this.renderer.clearPreview();
        this.controller.finishCalibration(a, b);
      }
      return;
    }

    if (this.tool === 'erase' && onElement) {
      const el = this.elementForNode(node);
      if (el) this.editor.apply(deleteElements, { ids: [el.id] });
      return;
    }

    // Sticky add-species: click element after element to attach the pending
    // plant to each (v1 AddSpecies tool). Esc leaves the tool.
    if (this.tool === 'addspecies') {
      const el = onElement ? this.elementForNode(node) : null;
      if (el && !el.data?.locked && this.store.state.pendingSpecies) {
        this.editor.apply(addSpecies, { elementId: el.id, plant: this.store.state.pendingSpecies });
      }
      return;
    }

    // Style eyedropper: first click picks up an element's appearance, further
    // clicks paint it onto other elements. Esc leaves the tool.
    if (this.tool === 'copystyle') {
      const el = onElement ? this.elementForNode(node) : null;
      if (!el || el.data?.locked) return;
      if (!this.controller.pickedStyle) this.controller.pickStyleFrom(el);
      else this.editor.apply(pasteStyle, { ids: [el.id], style: this.controller.pickedStyle });
      return;
    }

    if ((this.tool === 'polygon' || this.tool === 'line')) {
      this.addPolyVertex(this.renderer.pointerPlanPos());
      return;
    }

    if (this.tool === 'select') {
      // A plain click (no drag — Konva suppresses click after a drag) finalises
      // selection: additive presses added on press; here a plain click collapses
      // a multi-selection to the clicked element (or deselects), and an
      // additive tap on an already-selected element takes it out.
      const additive = this.isAdditive(e.evt);
      // A press on the resize box never deselects. A path's side bands lie
      // over its straight edges, so a plain click there still picks the side.
      if (this._pressOnTransformer) {
        if (this._pressTransformed) return;
        const sel = this.store.state.selection;
        const path = sel.length === 1 ? this.elements.find((x) => x.id === sel[0]) : null;
        const seg = path && this.segmentUnderPointer(path);
        if (seg) this.store.set({ selectedSegment: { id: path.id, index: seg.seg.index } });
        return;
      }
      const el = onElement ? this.elementForNode(node) : (this.isLabel(node) ? this.elementForLabel(node) : null);
      const picked = this.store.state.selectedSegment;
      // On the selected polygon/line, a click on an edge picks that segment
      // (v1); a click elsewhere on it drops the pick.
      const onSelectedPath = this._pressOnSelectedPath; this._pressOnSelectedPath = false;
      if (el && onElement && !additive && onSelectedPath && this.isSoleSelectedPath(el)) {
        const seg = this.segmentUnderPointer(el);
        this.store.set({ selectedSegment: seg ? { id: el.id, index: seg.seg.index } : null });
        return;
      }
      const pendingDeselect = this._pendingDeselect; this._pendingDeselect = null;
      if (el && !el.data?.locked && !additive) this.select(el.id, false);
      else if (el && additive && pendingDeselect === el.id) this.select(el.id, true);
      // Empty canvas drops a picked segment first, then the selection (v1).
      else if (!el && !additive && !this.lasso && picked) this.store.set({ selectedSegment: null });
      else if (!el && !additive && !this.lasso) this.store.set({ selection: [] });
    }
  }

  // View mode: a click (not the end of a drag-pan) selects the element under
  // the pointer and shows it in the Info tab; empty canvas clears the selection.
  onViewClick(e) {
    if (this._panMoved) return;
    const node = e.target;
    const el = this.isElement(node) ? this.elementForNode(node) : (this.isLabel(node) ? this.elementForLabel(node) : null);
    if (!el) { if (this.store.state.selection.length) this.store.set({ selection: [] }); return; }
    this.controller.selectAndShowInfo(el);
  }

  onDblClick(e) {
    // Two fingers lifting together look like a double-tap to Konva.
    if (this.pinchJustEnded()) return;
    if (this.poly) {
      // Only a double-click on one spot finishes — its second click landed on
      // the point the first one added. Two quick clicks in different places
      // are just two points.
      if (this._lastClickRepeated && this.finishPoly()) this._polyFinishedAt = performance.now();
      return;
    }
    // On a phone the double-tap that finished a shape can arrive a second time
    // (as dbltap and as the emulated dblclick) — it must not zoom in, or land
    // on the new shape's last vertex handle and delete that point.
    if (msSince(this._polyFinishedAt) < 500) return;
    const node = e.target;
    // The resize box isn't empty canvas, even when Konva reports the stage.
    if (this._pressOnTransformer) return;
    // Double-click empty canvas zooms in (works in view mode too).
    if (node === this.stage) {
      this.controller.zoomToScaleAt((this.store.state.view.scale || 1) * 1.5, this.stage.getPointerPosition());
      return;
    }
    if (this.viewOnly) return;
    // Double-click a vertex handle removes that vertex.
    if (this.isHandle(node) && node.getAttr('vkind') === 'anchor') {
      this.editor.apply(removeVertex, { id: node.getAttr('elId'), index: node.getAttr('vidx') });
      return;
    }
    if (this.isElement(node)) {
      const el = this.elementForNode(node);
      if (el?.data?.locked) return;
      const sel = this.store.state.selection;
      // Not on the selected polygon/line: its points are added with the
      // midpoint handles, and a double-click there is two segment picks.
      if (el && (el.type === 'polygon' || el.type === 'line') && sel.length === 1 && sel[0] === el.id) return;
      // On a shape: select all elements of the same type.
      if (el) this.controller.selectAllOfType(el.type);
    }
  }

  select(id, additive) {
    if (this.elements.find((el) => el.id === id)?.data?.locked) return;
    let sel = this.store.state.selection.slice();
    if (additive) sel = sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id];
    else sel = [id];
    this.store.set({ selection: sel });
    this.controller.showPropertiesForSelection();
  }

  // --- drag move ------------------------------------------------------------
  // Capture the other selected element nodes + start positions so a drag on one
  // moves the whole selection together.
  startGroupDrag(node) {
    this._group = null;
    if (!this.isElement(node)) return;
    const id = node.id();
    if (this.elementForNode(node)?.data?.locked) return;
    const sel = this.store.state.selection || [];
    if (sel.length < 2 || !sel.includes(id)) return;
    const others = [];
    for (const sid of sel) {
      if (sid === id || this.elements.find((el) => el.id === sid)?.data?.locked) continue;
      const n = this.renderer.nodeMap.get(sid);
      if (n) others.push({ node: n, x0: n.x(), y0: n.y() });
    }
    this._group = { leadId: id, leadX0: node.x(), leadY0: node.y(), others };
  }

  // Place the whole group from the lead: snap the lead, then put every other
  // element at its start + the lead's delta. Konva also drags the
  // transformer-attached siblings itself (each firing its own dragmove), so
  // this runs on every element dragmove — it's idempotent, and whichever event
  // comes last in a pointer move leaves everything where it belongs.
  dragGroup() {
    const g = this._group;
    const lead = this.renderer.nodeMap.get(g.leadId);
    if (!lead) return;
    if (snapStep(this.store.state)) lead.position({ x: this.snap(lead.x()), y: this.snap(lead.y()) });
    const dx = lead.x() - g.leadX0;
    const dy = lead.y() - g.leadY0;
    for (const o of g.others) o.node.position({ x: o.x0 + dx, y: o.y0 + dy });
  }

  onDragEnd(e) {
    const node = e.target;
    // Ignore the dragend echo from a sibling we stopDrag'd at gesture start.
    if (this._group && this.isElement(node) && node.id() !== this._group.leadId) return;
    if (!this._dragPrev) return;
    const prev = this._dragPrev; this._dragPrev = null;
    const group = this._group; this._group = null;
    // Cut short by a pinch: put everything back, no undo entry.
    if (this._abortGesture) { this._abortGesture = false; this.revertTo(prev); return; }

    if (this.isHandle(node)) { this.renderer.applyHandle(node); this.editor.commitGesture(prev); return; }

    if (this.isLabel(node)) {
      const el = this.elementForLabel(node);
      const c = el && centerOf(el);
      if (!c) return;
      el.data.labelX = node.x() - c.x;
      el.data.labelY = node.y() - c.y;
      this.editor.commitGesture(prev);
      return;
    }
    const el = this.elementForNode(node);
    if (!el || el.data?.locked) return;
    this.renderer.bakeNode(node, el);
    // Bake the rest of a moved multi-selection too.
    if (group) for (const o of group.others) { const elx = this.elementForNode(o.node); if (elx && !elx.data?.locked) this.renderer.bakeNode(o.node, elx); }
    this.editor.commitGesture(prev);
  }

  onTransformEnd() {
    if (!this._tfPrev) return;
    if (this._abortGesture) { this._abortGesture = false; const prev = this._tfPrev; this._tfPrev = null; this.revertTo(prev); return; }
    for (const node of this.renderer.transformer.nodes()) {
      const el = this.elementForNode(node);
      if (el && !el.data?.locked) this.renderer.bakeNode(node, el);
    }
    const prev = this._tfPrev; this._tfPrev = null;
    this.editor.commitGesture(prev);
  }

  // --- draw rect / ellipse --------------------------------------------------
  // Shared box math for preview + finish. Modifiers (v1 parity): Shift
  // constrains to a square, Alt draws from the centre. Ellipses are always
  // circles (circles on the wire — the preview matches what will be created).
  dragBox(start, pos, mods = {}) {
    let dx = pos.x - start.x; let dy = pos.y - start.y;
    if (mods.square) {
      const m = Math.max(Math.abs(dx), Math.abs(dy));
      dx = (dx < 0 ? -1 : 1) * m; dy = (dy < 0 ? -1 : 1) * m;
    }
    if (mods.fromCenter) return { cx: start.x, cy: start.y, w: Math.abs(dx) * 2, h: Math.abs(dy) * 2 };
    return { cx: start.x + dx / 2, cy: start.y + dy / 2, w: Math.abs(dx), h: Math.abs(dy) };
  }

  drawMods(tool, evt) {
    return { square: tool === 'ellipse' || !!evt?.shiftKey, fromCenter: !!evt?.altKey };
  }

  startDraw(tool, start) {
    start = this.snapPos(start);
    this.draw = { tool, start, current: start, mods: this.drawMods(tool, null) };
    const node = tool === 'ellipse'
      ? new Konva.Ellipse({ x: start.x, y: start.y, radiusX: 0, radiusY: 0 })
      : new Konva.Rect({ x: start.x, y: start.y, width: 0, height: 0 });
    this.stylePreview(node, tool);
    this.renderer.setPreview(node);
  }

  // The shape being drawn looks as it will once drawn (the draw tool's fill,
  // stroke and thickness). With no stroke a thin dashed guide stands in, so an
  // unfilled outline is still visible while drawing.
  stylePreview(node, tool) {
    const data = drawStyleData(this.store.state.drawStyles, tool);
    this.renderer.styleNode(node, { type: tool, data });
    if (!data.strokeColor) { node.stroke('#087332'); node.strokeWidth(1); node.strokeScaleEnabled(false); node.dash([4, 4]); }
  }

  // Data for a new `tool` shape: its geometry + the draw tool's style/dates.
  newShapeData(tool, geometry) {
    return {
      ...geometry,
      ...drawStyleData(this.store.state.drawStyles, tool, Math.floor(Math.random() * 100000)),
      labels: [], labelX: 0, labelY: 0,
    };
  }

  updateDraw(pos, evt) {
    pos = this.snapPos(pos);
    this.draw.current = pos;
    if (evt) this.draw.mods = this.drawMods(this.draw.tool, evt);
    const n = this.renderer.previewNode; if (!n) return;
    const b = this.dragBox(this.draw.start, pos, this.draw.mods);
    if (this.draw.tool === 'ellipse') {
      n.position({ x: b.cx, y: b.cy });
      n.radiusX(b.w / 2); n.radiusY(b.h / 2);
    } else {
      n.position({ x: b.cx - b.w / 2, y: b.cy - b.h / 2 });
      n.width(b.w); n.height(b.h);
    }
    n.getLayer().batchDraw();
  }

  finishDraw() {
    const { tool, start, current, mods } = this.draw;
    this.draw = null;
    this.renderer.clearPreview();
    const b = this.dragBox(start, current, mods);
    if (b.w < MIN_SIZE && b.h < MIN_SIZE) return;
    const data = this.newShapeData(tool, { x: b.cx, y: b.cy, width: b.w, height: tool === 'ellipse' ? b.w : b.h });
    if (tool === 'rectangle') data.rotation = 0;
    const el = { id: `el-${Date.now()}-${Math.round(start.x)}`, type: tool, speciesIds: [], data };
    this.editor.apply(addElements, { elements: [el] });
    this.controller.showPropertiesForSelection();
    this.controller.applyTool('select');
    window.posthog?.capture('planner_element_created', { element_type: tool, planner_version: 'v2' });
  }

  // --- draw polygon / line -------------------------------------------------
  // Each click adds a segment of the draw style's type (straight / curve /
  // bezier, with v1's default bow); a curved polygon closes the same way.
  // `points` holds the coordinate entries as drawn.
  segmentTypeFor(tool) { return this.store.state.drawStyles?.[tool]?.segmentType || 'straight'; }

  addPolyVertex(pos) {
    // A click on the last point (the second half of a double-click) adds nothing.
    if (this.poly) {
      const [lx, ly] = this.lastPolyAnchor();
      const scale = this.store.state.view?.scale || 1;
      this._lastClickRepeated = Math.hypot(pos.x - lx, pos.y - ly) * scale < this.samePointPx();
      if (this._lastClickRepeated) return;
    }
    this._lastClickRepeated = false;
    if (!this.poly) this.poly = { tool: this.tool, points: [[pos.x, pos.y]] };
    else this.poly.points.push(newSegmentEntry(this.segmentTypeFor(this.poly.tool), this.lastPolyAnchor(), [pos.x, pos.y]));
    this.renderPolyPreview(pos);
  }

  lastPolyAnchor() { const c = this.poly.points[this.poly.points.length - 1]; return [c[0], c[1]]; }

  // The coordinates as they'd be if the shape were finished now (optionally
  // with the segment to `cursor` still to come).
  // `close: false` leaves off a curved polygon's closing segment (the preview).
  polyCoordinates(cursor = null, { close = true } = {}) {
    const { tool, points } = this.poly;
    const type = this.segmentTypeFor(tool);
    const coords = points.slice();
    if (cursor) coords.push(newSegmentEntry(type, this.lastPolyAnchor(), [cursor.x, cursor.y]));
    if (close && tool === 'polygon' && type !== 'straight' && coords.length > 1) {
      const last = coords[coords.length - 1];
      coords.push(newSegmentEntry(type, [last[0], last[1]], [coords[0][0], coords[0][1]], true));
    }
    return coords;
  }

  updatePoly(pos) { if (this.poly) this.renderPolyPreview(pos); }

  // An open outline while drawing — no closing edge and no fill, so an
  // unfinished polygon doesn't look like a finished one.
  renderPolyPreview(cursor) {
    const node = new Konva.Path({
      data: coordsToPathData(this.polyCoordinates(cursor, { close: false }), false),
      lineCap: 'round', lineJoin: 'round',
    });
    this.stylePreview(node, this.poly.tool);
    node.fillEnabled(false);
    this.renderer.setPreview(node);
  }

  // Finishes the shape if it has enough points (a polygon needs 3, a line 2) —
  // otherwise drawing carries on. Returns whether it finished.
  finishPoly() {
    const poly = this.poly;
    if (!poly || poly.points.length < (poly.tool === 'polygon' ? 3 : 2)) return false;
    const coordinates = this.polyCoordinates();
    this.poly = null; this.renderer.clearPreview();
    const data = this.newShapeData(poly.tool, { coordinates });
    const el = { id: `el-${Date.now()}`, type: poly.tool, speciesIds: [], data };
    this.editor.apply(addElements, { elements: [el] });
    this.controller.showPropertiesForSelection();
    this.controller.applyTool('select');
    window.posthog?.capture('planner_element_created', { element_type: poly.tool, planner_version: 'v2' });
    return true;
  }

  cancelPoly() { this.poly = null; this.renderer.clearPreview(); }

  // --- pan / zoom -----------------------------------------------------------
  startPan() {
    const p = this.stage.getPointerPosition();
    const v = this.store.state.view;
    this.pan = { sx: p.x, sy: p.y, px: v.posX, py: v.posY };
    this._panMoved = false;
  }

  updatePan() {
    const p = this.stage.getPointerPosition();
    if (Math.hypot(p.x - this.pan.sx, p.y - this.pan.sy) > 3) this._panMoved = true;
    const view = { ...this.store.state.view, posX: this.pan.px + (p.x - this.pan.sx), posY: this.pan.py + (p.y - this.pan.sy) };
    this.store.set({ view }, { source: 'pan' });
  }

  // --- lasso select ---------------------------------------------------------
  // Shift-drag adds the boxed elements to the selection, Alt-drag removes them
  // (v1 CTRL/SHIFT-drag parity, on the keys v2 already uses for click-toggling).
  startLasso(start, evt) {
    const mode = evt?.altKey ? 'subtract' : (this.isAdditive(evt) ? 'add' : 'replace');
    this.lasso = { start, current: start, mode, base: this.store.state.selection.slice() };
  }

  updateLasso(pos) {
    this.lasso.current = pos;
    const { start, mode } = this.lasso;
    const node = new Konva.Rect({
      x: Math.min(start.x, pos.x), y: Math.min(start.y, pos.y),
      width: Math.abs(pos.x - start.x), height: Math.abs(pos.y - start.y),
      fill: mode === 'subtract' ? 'rgba(200,40,40,0.06)' : 'rgba(8,115,50,0.06)',
      stroke: mode === 'subtract' ? '#c02828' : '#087332', strokeWidth: 1, dash: [4, 4],
    });
    this.renderer.setPreview(node);
  }

  finishLasso() {
    const { start, current, mode, base } = this.lasso; this.lasso = null; this.renderer.clearPreview();
    const x1 = Math.min(start.x, current.x); const x2 = Math.max(start.x, current.x);
    const y1 = Math.min(start.y, current.y); const y2 = Math.max(start.y, current.y);
    if (x2 - x1 < 3 && y2 - y1 < 3) return; // a click, not a drag — let onClick handle it
    this._suppressClick = true;
    // Overlap, not centre-in-box: a large bed is picked up even when its
    // centre falls outside the marquee.
    const boxed = this.elements.filter((el) => !el.data?.locked && elementInBox(el, x1, y1, x2, y2)).map((el) => el.id);
    let sel;
    if (mode === 'add') sel = [...new Set([...base, ...boxed])];
    else if (mode === 'subtract') { const drop = new Set(boxed); sel = base.filter((id) => !drop.has(id)); }
    else sel = boxed;
    this.store.set({ selection: sel });
    this.controller.showPropertiesForSelection();
  }

  updatePlacementGhost(pos) {
    const sp = this.store.state.pendingSpecies;
    if (!sp) return;
    const r = plantDiameter(sp) / 2;
    this.renderer.setPreview(new Konva.Ellipse({
      x: pos.x, y: pos.y, radiusX: r, radiusY: r,
      fill: 'rgba(114,180,95,0.4)', stroke: '#087332', strokeWidth: 1, dash: [4, 4],
    }));
  }

  // --- touch: pinch-zoom + long-press --------------------------------------
  onTouchStart(e) {
    const touches = e.evt.touches;
    if (touches && touches.length === 2) { this.startPinch(touches); return; }
    const t = touches && touches[0];
    if (!t) return;
    this._abortGesture = false; // a fresh gesture
    this._lp = { x: t.clientX, y: t.clientY, hits: this.elementsAtPointer(), planPos: this.renderer.pointerPlanPos() };
    clearTimeout(this._lpTimer);
    this._lpTimer = setTimeout(() => {
      if (this.gestureActive()) return;
      this._lpFired = true;
      this.draw = this.lasso = this.pan = null; this.renderer.clearPreview();
      this.renderer.stopAllDrag();
      if (this._lp) this.controller.openContextForHits(this._lp.x, this._lp.y, this._lp.hits, this._lp.planPos);
    }, 500);
  }

  onTouchMove(e) {
    const touches = e.evt.touches;
    if (touches && touches.length === 2) { e.evt.preventDefault(); this.updatePinch(touches); return; }
    const t = touches && touches[0];
    if (this._lp && t && Math.hypot(t.clientX - this._lp.x, t.clientY - this._lp.y) > 10) this.cancelLongPress();
  }

  onTouchEnd(e) {
    const n = e.evt.touches ? e.evt.touches.length : 0;
    if (n < 2 && this._pinching) this._pinchEndedAt = performance.now();
    if (n < 2) { this._pinching = false; this._pinchDist = 0; this._pinchMid = null; }
    this.cancelLongPress();
  }

  pinchJustEnded() { return this._pinching || msSince(this._pinchEndedAt) < 400; }

  isEmulatedMouse(e) {
    return e.evt instanceof MouseEvent && msSince(this._lastTouchAt) < TOUCH_TRAIL_MS;
  }

  // Two clicks closer than this (screen px) are the same point; a finger is
  // less precise than a mouse.
  samePointPx() { return msSince(this._lastTouchAt) < TOUCH_TRAIL_MS ? 16 : 6; }

  gestureActive() { return Konva.isDragging() || this.renderer.transformer.isTransforming(); }

  cancelLongPress() { clearTimeout(this._lpTimer); this._lpTimer = null; this._lp = null; }

  // A second finger: whatever the first one started (a drag, a resize, a
  // press-to-select, a drawing, a marquee) is called off and undone, so the
  // two fingers only pan and zoom.
  startPinch(touches) {
    this._pinching = true;
    this.cancelLongPress();
    this.draw = this.lasso = this.pan = null; this.renderer.clearPreview();
    this._labelPress = null; this.endCoveredPress();
    if (this._dragPrev || this.renderer.transformer.isTransforming()) this._abortGesture = true;
    this.renderer.stopAllDrag();
    if (this.renderer.transformer.isTransforming()) this.renderer.transformer.stopTransform();
    const before = this._touchSelection;
    const sel = this.store.state.selection;
    if (before && (before.length !== sel.length || before.some((id, i) => id !== sel[i]))) this.store.set({ selection: before });
    const m = this.pinchMetrics(touches);
    this._pinchDist = this._pinchStartDist = m.dist; this._pinchMid = m.mid; this._pinchZoom = null;
  }

  updatePinch(touches) {
    this._pinching = true;
    const m = this.pinchMetrics(touches);
    if (!this._pinchDist) { this._pinchDist = this._pinchStartDist = m.dist; this._pinchMid = m.mid; this._pinchZoom = null; return; }
    const view = this.store.state.view;
    const old = view.scale || 1;
    // Zoom engages once the spread has clearly changed, measured from the edge
    // of the dead zone (so it starts smoothly, without a jump); before that
    // the fingers only pan.
    const spread = m.dist / this._pinchStartDist;
    if (!this._pinchZoom && Math.abs(spread - 1) > PINCH_ZOOM_DEADZONE) {
      this._pinchZoom = { dist: this._pinchStartDist * (spread > 1 ? 1 + PINCH_ZOOM_DEADZONE : 1 - PINCH_ZOOM_DEADZONE), scale: old };
    }
    const z = this._pinchZoom;
    const scale = z ? Math.min(50, Math.max(0.02, z.scale * (m.dist / z.dist) ** PINCH_ZOOM_EXP)) : old;
    const mx = (m.mid.x - view.posX) / old;
    const my = (m.mid.y - view.posY) / old;
    // zoom around the pinch midpoint, and pan by however far the midpoint moved
    const posX = m.mid.x - mx * scale + (m.mid.x - this._pinchMid.x);
    const posY = m.mid.y - my * scale + (m.mid.y - this._pinchMid.y);
    this.store.set({ view: { scale, posX, posY } }, { source: scale === old ? 'pan' : 'zoom' });
    this._pinchDist = m.dist; this._pinchMid = m.mid;
  }

  // Put the plan back as it was at `prev` (a snapshot) without an undo entry.
  revertTo(prev) {
    this.store.set({ elements: prev.elements, layers: prev.layers, settings: prev.settings }, { source: 'commit' });
  }

  pinchMetrics(touches) {
    const r = this.stage.container().getBoundingClientRect();
    const a = touches[0]; const b = touches[1];
    return {
      dist: Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY),
      mid: { x: (a.clientX + b.clientX) / 2 - r.left, y: (a.clientY + b.clientY) / 2 - r.top },
    };
  }

  onWheel(e) {
    e.evt.preventDefault();
    const view = this.store.state.view;
    const old = view.scale || 1;
    const pointer = this.stage.getPointerPosition();
    const mx = (pointer.x - view.posX) / old; const my = (pointer.y - view.posY) / old;
    const factor = e.evt.deltaY > 0 ? 0.92 : 1.08;
    const scale = Math.min(50, Math.max(0.02, old * factor));
    this.store.set({ view: { scale, posX: pointer.x - mx * scale, posY: pointer.y - my * scale } }, { source: 'zoom' });
  }
}

